import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, ChevronLeft, ChevronRight, Eye, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import { showToast } from '../../components/Toast';
import { showConfirm } from '../../components/ConfirmDialog';

interface Trainer {
  _id: string;
  firstName: string;
  lastName: string;
  joinedDate: string;
  specialization: string;
  status: 'Active' | 'Pending' | 'Inactive';
  clients: number;
  rating: number;
  profileImage?: string;
  instructorProfile?: {
    specialties?: string[];
    bio?: string;
    experienceYears?: number;
    certifications?: string[];
  };
  accountStatus?: string;
  createdAt?: string;
}

const TrainersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch trainers
  const { data: trainersData = [], isLoading } = useQuery({
    queryKey: ['trainers'],
    queryFn: async () => {
      const data = await userService.getAllTrainers();
      console.log('🔍 [TrainersPage] Raw trainers data from backend:', data);
      console.log('🔍 [TrainersPage] Trainers with Pending status:', data.filter((t: any) => t.accountStatus === 'Pending'));
      return data;
    },
    staleTime: 0, // Always refetch
    refetchOnMount: true,
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['trainerStats'],
    queryFn: () => userService.getTrainerStats(),
  });

  // Mutation for updating trainer status
  const updateStatusMutation = useMutation({
    mutationFn: ({ trainerId, status }: { trainerId: string; status: 'Active' | 'Pending' | 'Banned' }) =>
      userService.updateTrainerStatus(trainerId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainers'] });
      queryClient.invalidateQueries({ queryKey: ['trainerStats'] });
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to update trainer status', 'error');
    },
  });

  // Transform and filter trainers
  const trainers: Trainer[] = Array.isArray(trainersData)
    ? trainersData.map((trainer: any) => ({
        _id: trainer._id,
        firstName: trainer.firstName || '',
        lastName: trainer.lastName || '',
        joinedDate: trainer.createdAt ? new Date(trainer.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : 'Unknown',
        specialization: trainer.instructorProfile?.specialties?.[0] || 'General Training',
        status: trainer.accountStatus || 'Active',
        clients: trainer.clientCount || 0,
        rating: trainer.rating || 0,
        profileImage: trainer.profilePhoto || trainer.profilePhotoUrl,
        instructorProfile: {
          specialties: trainer.instructorProfile?.specialties || [],
          bio: trainer.instructorProfile?.bio || '',
          experienceYears: trainer.instructorProfile?.experienceYears || 0,
          certifications: trainer.instructorProfile?.certifications || [],
        },
        accountStatus: trainer.accountStatus,
      }))
    : [];

  const filteredTrainers = trainers.filter(trainer =>
    trainer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.specialization.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredTrainers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTrainers = filteredTrainers.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 4;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 1; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  const statCards = [
    { label: 'Total Trainers', value: stats?.totalTrainers || '0', change: '+12%', icon: '👥' },
    { label: 'Active Trainers', value: stats?.activeTrainers || '0', change: '+5%', icon: '✓' },
    { 
      label: 'Pending Requests', 
      value: stats?.pendingTrainers || '0', 
      change: trainers.filter(t => t.status === 'Pending').length > 0 ? 'Needs Review' : '', 
      icon: '⏳' 
    },
    { label: 'Average Rating', value: stats?.avgRating || '0', change: '+0.1', icon: '⭐' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500 bg-opacity-20 text-green-400';
      case 'Pending':
        return 'bg-yellow-500 bg-opacity-20 text-yellow-400';
      case 'Inactive':
      case 'Banned':
        return 'bg-red-500 bg-opacity-20 text-red-400';
      default:
        return 'bg-gray-500 bg-opacity-20 text-gray-400';
    }
  };

  const handleApproveTrainer = (trainerId: string) => {
    updateStatusMutation.mutate({ trainerId, status: 'Active' });
  };

  const handleRejectTrainer = (trainerId: string) => {
    const trainer = trainers.find(t => t._id === trainerId);
    const trainerName = trainer ? `${trainer.firstName} ${trainer.lastName}` : 'this trainer';
    
    showConfirm({
      title: 'Reject Trainer',
      message: `Are you sure you want to reject ${trainerName}? This action cannot be undone.`,
      confirmText: 'Reject',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: () => updateStatusMutation.mutate({ trainerId, status: 'Banned' }),
    });
  };

  const handleViewTrainer = (trainerId: string) => {
    navigate(`/trainers/${trainerId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading trainers...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Trainers Directory</h1>
              <p className="text-gray-400">Manage and monitor all fitness trainers in your network</p>
            </div>
            
            {/* Pending Requests Badge */}
            {trainers.filter(t => t.status === 'Pending').length > 0 && (
              <div className="bg-yellow-500 bg-opacity-20 border border-yellow-500 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                  <div>
                    <p className="text-yellow-400 font-semibold">
                      {trainers.filter(t => t.status === 'Pending').length} Pending Request{trainers.filter(t => t.status === 'Pending').length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-yellow-300 text-sm">Requires your attention</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-8 flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Search trainers, skills, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-[#23F47C]"
            />
          </div>
          
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <span className="text-gray-400 text-sm">{stat.label}</span>
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-white">{stat.value}</span>
                {stat.change && <span className="text-green-400 text-sm">{stat.change}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Pending Requests Section */}
        {trainers.filter(t => t.status === 'Pending').length > 0 && (
          <div className="mb-8">
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <div className="p-6 border-b border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-2">Pending Trainer Requests</h2>
                <p className="text-gray-400">Review and approve trainer applications</p>
              </div>
              
              <div className="p-6">
                <div className="grid gap-6">
                  {trainers.filter(t => t.status === 'Pending').map((trainer) => (
                    <div key={trainer._id} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Profile Image */}
                          <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                            {trainer.profileImage ? (
                              <img
                                src={trainer.profileImage}
                                alt={trainer.firstName}
                                className="w-16 h-16 rounded-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <span className="text-gray-300 text-lg font-semibold">
                                {trainer.firstName.charAt(0)}{trainer.lastName.charAt(0)}
                              </span>
                            )}
                          </div>
                          
                          {/* Trainer Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-xl font-bold text-white">
                                {trainer.firstName} {trainer.lastName}
                              </h3>
                              <span className="px-3 py-1 bg-yellow-500 bg-opacity-20 text-yellow-400 rounded-full text-sm font-medium">
                                Pending Approval
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Specialization</p>
                                <p className="text-gray-300">{trainer.specialization}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Applied Date</p>
                                <p className="text-gray-300">{trainer.joinedDate}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Experience</p>
                                <p className="text-gray-300">
                                  {trainer.instructorProfile?.experienceYears ? 
                                    `${trainer.instructorProfile.experienceYears} years` : 
                                    'Not specified'
                                  }
                                </p>
                              </div>
                            </div>

                            {/* Specialties */}
                            {trainer.instructorProfile?.specialties && trainer.instructorProfile.specialties.length > 0 && (
                              <div className="mb-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Skills & Specialties</p>
                                <div className="flex flex-wrap gap-2">
                                  {trainer.instructorProfile.specialties.map((specialty: string, index: number) => (
                                    <span 
                                      key={index}
                                      className="px-3 py-1 bg-[#23F47C] bg-opacity-20 text-[#23F47C] rounded-full text-sm"
                                    >
                                      {specialty}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Bio Preview */}
                            {trainer.instructorProfile?.bio && (
                              <div className="mb-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">About</p>
                                <p className="text-gray-300 text-sm" style={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}>
                                  {trainer.instructorProfile.bio.length > 150 
                                    ? `${trainer.instructorProfile.bio.substring(0, 150)}...` 
                                    : trainer.instructorProfile.bio
                                  }
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-3 ml-6">
                          <button 
                            onClick={() => handleViewTrainer(trainer._id)}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all"
                          >
                            <Eye size={16} />
                            View Details
                          </button>
                          
                          <button 
                            onClick={() => handleApproveTrainer(trainer._id)}
                            disabled={updateStatusMutation.isPending}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all disabled:opacity-50"
                          >
                            <Check size={16} />
                            Approve
                          </button>
                          
                          <button 
                            onClick={() => handleRejectTrainer(trainer._id)}
                            disabled={updateStatusMutation.isPending}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50"
                          >
                            <X size={16} />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trainers Table */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Trainer Profile</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Specialization</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Clients</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Rating</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTrainers.map((trainer) => (
                  <tr key={trainer._id} className="border-b border-gray-800 hover:bg-gray-800 bg-opacity-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                          {trainer.profileImage ? (
                            <img
                              src={trainer.profileImage}
                              alt={trainer.firstName}
                              className="w-10 h-10 rounded-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="text-gray-300 text-sm font-medium">
                              {trainer.firstName.charAt(0)}{trainer.lastName.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{trainer.firstName} {trainer.lastName}</p>
                          <p className="text-gray-400 text-sm">Joined {trainer.joinedDate}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{trainer.specialization}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(trainer.status)}`}>
                        {trainer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white">{trainer.clients}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {trainer.rating > 0 ? (
                          <>
                            <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#23F47C]"
                                style={{ width: `${(trainer.rating / 5) * 100}%` }}
                              />
                            </div>
                            <span className="text-white font-medium">{trainer.rating.toFixed(1)}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {trainer.status === 'Pending' && (
                          <>
                            <button 
                              onClick={() => handleApproveTrainer(trainer._id)}
                              disabled={updateStatusMutation.isPending}
                              className="flex items-center gap-1 px-3 py-1 bg-green-500 bg-opacity-20 text-green-400 rounded text-sm hover:bg-opacity-30 transition-all disabled:opacity-50"
                              title="Approve Trainer"
                            >
                              <Check size={14} />
                              Approve
                            </button>
                            <button 
                              onClick={() => handleRejectTrainer(trainer._id)}
                              disabled={updateStatusMutation.isPending}
                              className="flex items-center gap-1 px-3 py-1 bg-red-500 bg-opacity-20 text-red-400 rounded text-sm hover:bg-opacity-30 transition-all disabled:opacity-50"
                              title="Reject Trainer"
                            >
                              <X size={14} />
                              Reject
                            </button>
                          </>
                        )}
                        <button 
                          onClick={() => handleViewTrainer(trainer._id)}
                          className="p-2 text-gray-400 hover:text-[#23F47C] transition-all"
                          title="View Trainer Details"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
            <div className="text-gray-400 text-sm">
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} trainers
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={20} />
              </button>

              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                    currentPage === page
                      ? 'bg-[#23F47C] text-gray-900'
                      : 'border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C]'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainersPage;
